/**
 * Identificador único da instalação do app no navegador (X-Client-Id).
 *
 * - Gerado uma vez por navegador/perfil e persistido em localStorage (chave: tdr.client_id).
 * - Enviado em todo request à api-tdr via interceptor (ver api.js).
 * - Não muda com login/logout: representa o navegador, não o usuário/CNPJ.
 * - Fallback em memória quando localStorage está indisponível (não bloqueia o app).
 */

const STORAGE_KEY = 'tdr.client_id'
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

let memoryClientId = null

export function generateUuidV4 () {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function readFromStorage () {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return UUID_V4_REGEX.test(value) ? value : null
  } catch {
    return null
  }
}

function writeToStorage (value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value)
    return true
  } catch {
    return false
  }
}

/**
 * @returns {string} UUID v4 estável por navegador (ou da sessão atual quando localStorage indisponível).
 */
function getClientId () {
  const stored = readFromStorage()
  if (stored) return stored
  if (memoryClientId) return memoryClientId

  const fresh = generateUuidV4()
  const persisted = writeToStorage(fresh)
  if (!persisted) memoryClientId = fresh
  return fresh
}

export default {
  getClientId,
  generateUuidV4,
}
